import { useMemo, useState } from "react";
import type { ComponentProps } from "react";

import { Table } from "@similarweb/pro-logic-components";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Box } from "@similarweb/ui-components/foundations";

import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { CompareTabsContainer } from "app/components/compareTabs/CompareTabsContainer";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { ADDON_TYPE_NAME_DICT } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { translate } from "app/filters/i18nFilter";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import {
    FIRST_PAGE,
    getNoDataObject,
    PAGE_SIZE_LARGE,
} from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { getPaidExcelProps } from "app/services/PaidExcelService/getPaidExcelProps";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { MixedScrapperBanner } from "../../common/ads/MixedScrapperBanner";

import { buildCampaignsExportParams } from "./campaignsExportParams";
import { getColumns } from "./DisplayAdvertisingCampaignsColumns";
import { useExcelCount } from "./useExcelCount";
import { useTableRows } from "./useTableRows";

export const initialTableSort: ISortProps = { asc: false, sort: "lastSeen" };

const CAMPAIGNS_EXCEL_ENDPOINT = "/api/AdIntelligence/Advertiser/Campaigns/Excel";
const SNAPSHOT_EXCEL_MAX_MONTHS = 3;

const sortAdapter = (sort: ISortProps) => {
    switch (sort.sort) {
        case "activeDays":
            return { asc: sort.asc, sort: "Duration" };
        default:
            return sort;
    }
};

export const DisplayAdvertisingCampaigns = () => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
    const [search, setSearch] = useState<string>(params.includeTerms ?? "");
    const [sort, setSort] = useState<ISortProps>({
        sort: params.sort ?? initialTableSort.sort,
        asc: params.asc ? params.asc === "true" : initialTableSort.asc,
    });
    const isCompare = chosenSites.isCompare();
    const selectedDomain = params.campaignsSelectedSite ?? params.key?.split(",")[0] ?? "";
    const { isWindow } = DurationService.getDurationData(params.duration ?? "").raw;
    const snapshotMonthsSpan = DurationService.getMonthsFromDuration(params.duration ?? "");
    const isExcelDownloadEnabled = isWindow || snapshotMonthsSpan <= SNAPSHOT_EXCEL_MAX_MONTHS;
    const { hasAddonsHub } = useAddonsState();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();
    const isExcelAllowed = swSettings.current.resources.IsExcelAllowed;

    const paidExcelProps = useMemo(() => (isPaidExcelAllowed() ? getPaidExcelProps() : null), []);

    const {
        data: rawData,
        isLoading,
        isError,
    } = useTableRows(selectedDomain, currentPage, PAGE_SIZE_LARGE, search, sortAdapter(sort));
    const data = rawData || { totalCount: 0, items: [], startDate: undefined };

    const { excelCount } = useExcelCount(
        selectedDomain,
        search,
        sortAdapter(sort),
        !!selectedDomain && isExcelAllowed && isExcelDownloadEnabled && !isCompare,
    );

    const excelDownloadUrl = useMemo(() => {
        if (!isExcelAllowed || !isExcelDownloadEnabled || isCompare) {
            return "";
        }
        const apiParams = buildCampaignsExportParams(params, selectedDomain, search, sortAdapter(sort));
        const queryParams = DefaultFetchService.getInstance().requestParams(apiParams);
        return `${CAMPAIGNS_EXCEL_ENDPOINT}?${queryParams}`;
    }, [
        isExcelAllowed,
        isExcelDownloadEnabled,
        isCompare,
        params.duration,
        params.country,
        selectedDomain,
        search,
        sort,
    ]);

    const handleExcelDownloaded = () => {
        allTrackers.dispatchTrackEvent("Download", "submit-ok", "Campaigns/Excel");
    };

    const columns = useMemo(
        () =>
            getColumns(data.totalCount, currentPage, PAGE_SIZE_LARGE, sort, params) as ColumnsType<
                (typeof data.items)[number]
            >,
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- specific properties used: data.totalCount, sort.sort/asc, params.campaignsSelectedSite
        [data.totalCount, currentPage, sort.sort, sort.asc, params.campaignsSelectedSite],
    );

    const handleSearchInputChange = (value: string) => {
        proNavigator.updateQueryParams(
            {
                ...params,
                includeTerms: value,
            },
            { reload: false },
        );
        allTrackers.dispatchTrackEvent("Search Bar", "click", `Table/${value}`);
        setSearch(value);
        setCurrentPage(FIRST_PAGE);
    };

    const handleSort: ComponentProps<typeof Table<(typeof data.items)[number]>>["onSort"] = (sorter) => {
        proNavigator.updateQueryParams(
            {
                ...params,
                sort: sorter.columnKey,
                asc: sorter.order === "ascend",
            },
            { reload: false },
        );
        setSort({
            sort: sorter.columnKey as string,
            asc: sorter.order === "ascend",
        });
    };

    const noDataObject = getNoDataObject(isError);

    const onSelectSite = (selectedSite: string) => {
        allTrackers.dispatchTrackEvent("Tab", "click", `Filter Domain/${selectedSite}`);
        const currentName = proNavigator.current()?.name;
        if (currentName) {
            void proNavigator.go(currentName, {
                ...proNavigator.getParams(),
                campaignsSelectedSite: selectedSite,
            });
        }
    };

    return (
        <>
            <MixedScrapperBanner newScrapingDateString={data?.startDate ?? undefined} />
            <Box
                width="100%"
                height="initial"
                background={colorsPalettes.carbon[0]}
                borderRadius="6px"
            >
                {isCompare ? (
                    <CompareTabsContainer
                        onSelectSite={onSelectSite}
                        keyName="campaignsFirstLoads"
                        domainQueryParamName={"campaignsSelectedSite"}
                        hideResults={true}
                    />
                ) : null}
                <Table
                    columnConfig={columns}
                    data={data?.items ?? []}
                    isLoading={isLoading}
                    loaderRowCount={13}
                    onSort={handleSort}
                    localStorageKey="AdIntelligenceAdvertiserCampaigns"
                    pagination={{
                        pageSize: PAGE_SIZE_LARGE,
                        total: data?.totalCount ?? 0,
                        current: currentPage,
                        onChange: (_page, _pageSize, newPage) => setCurrentPage(newPage),
                    }}
                    noData={noDataObject}
                >
                    <Table.TableTop borderBottom>
                        <Table.TableTop.Filters
                            filters={[
                                {
                                    filter: "search",
                                    initialValue: search?.[0] ?? "",
                                    placeholder: translate(
                                        "ad.intelligence.advertiser.campaigns.table.search.placeholder",
                                    ),
                                    onSearchChange: handleSearchInputChange,
                                },
                            ]}
                        />
                        <Table.TableTop.Settings
                            excelExportConfig={{
                                ...(paidExcelProps ?? {}),
                                withPaidExcel: !!paidExcelProps,
                                excelDownloadUrl,
                                totalCount: excelCount,
                                onExcelDownloaded: handleExcelDownloaded,
                                hasAddonHub: hasAddonsHub,
                                onCtaClicked: () => handlePurchaseAddonClick(ADDON_TYPE_NAME_DICT.DataCreditsDynamic),
                            }}
                        />
                    </Table.TableTop>
                </Table>
            </Box>
        </>
    );
};
